import { useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonIconText, FileDropzone, PrimaryButton } from '@/components';

interface ContractDocumentUploadProps {
  file: File | null;
  onFileSelect: (file: File | null) => void;
  disabled?: boolean;
  // When provided, an explicit upload button is shown that triggers an immediate upload.
  // Omitted in the create flow, where the parent uploads the file after creating the contract.
  onUpload?: () => void;
  isUploading?: boolean;
  collapsed?: boolean;
  showDropzone?: boolean;
}

// ContractDocumentUpload is the shared PDF picker used by both the contract create form and
// the active-contract document section, while allowing edit mode to start from an add button.
export function ContractDocumentUpload({
  file,
  onFileSelect,
  disabled = false,
  onUpload,
  isUploading = false,
  collapsed = false,
  showDropzone = false,
}: ContractDocumentUploadProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const isInputDisabled = disabled || isUploading;
  const hasFile = Boolean(file)
  const shouldShowDropzone = !collapsed || showDropzone || hasFile;

  const handleAddDocument = () => {
    if (!isInputDisabled) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    onFileSelect(selectedFile);
  };

  return (
    <div className="space-y-2 flex-col mt-4">
      {collapsed && (
        <>
          <input
            ref={inputRef}
            type="file"
            onChange={handleFileChange}
            className="hidden"
            disabled={isInputDisabled}
            accept=".pdf"
          />
          {!shouldShowDropzone &&
            <ButtonIconText
              type="button"
              iconName="upload"
              onClick={handleAddDocument}
              disabled={isInputDisabled}
            >
              {t('serviceContracts.documents.addDocument')}
            </ButtonIconText>
          }
        </>
      )}
      {shouldShowDropzone && (
        <FileDropzone
          file={file}
          onFileSelect={onFileSelect}
          disabled={isInputDisabled}
          accept=".pdf"
          prompt={t('serviceContracts.documents.dropFile')}
        />
      )}
      {onUpload && hasFile && (
        <PrimaryButton
          type="button"
          onClick={onUpload}
          disabled={!file || disabled}
          isLoading={isUploading}
          size="sm"
        >
          {t('serviceContracts.documents.uploadButton')}
        </PrimaryButton>
      )}
    </div>
  );
}
